import { useState, useEffect } from "react";
import useCRUD from "../../hooks/useCRUD";
import Spinner from "../Spinner/Spinner";
import AssignDriverModal from "./AssignDriverModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLightbulb,
  faUsers,
  faCircleCheck,
  faMapPin,
  faLocationDot,
  faGear,
} from "@fortawesome/free-solid-svg-icons";
import GeocodePromptModal from "../Elements/GeocodePromptModal";
import {
  geocodeParticipant,
  fetchAccountSettings,
  hasParticipantCoords,
} from "../../utils/geocode";
import { formatPhoneNumber } from "../../utils/phoneNumberUtils";
import NotificationPauseModal from "../Group/NotificationPauseModal";

const ParticipantsList = ({
  event,
  refreshData,
  eventParticipations,
  isEventExpired,
  suggestions,
}) => {
  const [participants, setParticipants] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const [participantToAssign, setParticipantToAssign] = useState(null);
  const [pauseParticipant, setPauseParticipant] = useState(null);

  const [isAssignDriverModalOpen, setIsAssignDriverModalOpen] = useState(false);
  const [driversWithCapacity, setDriversWithCapacity] = useState([]);

  const { updateItem: updateEventParticipation } = useCRUD(
    "/api/event-participations",
  );

  const [geocodeModal, setGeocodeModal] = useState({
    open: false,
    participant: null,
  });

  const runGeocode = async (participantId, extras = {}) => {
    try {
      setIsLoading(true);
      await geocodeParticipant(participantId, extras);
      refreshData();
    } catch (err) {
      console.error("Geocode failed:", err);
      alert(err.message || "Failed to fetch GPS coordinates.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFillGps = async (participant) => {
    try {
      const account = await fetchAccountSettings();
      const hasBase = !!(
        account?.baseCity ||
        account?.baseState ||
        account?.baseCountry
      );
      if (hasBase) {
        await runGeocode(participant.participantId);
      } else {
        setGeocodeModal({ open: true, participant });
      }
    } catch (err) {
      console.error(err);
      setGeocodeModal({ open: true, participant });
    }
  };

  const handleGeocodeModalSubmit = async ({ city, state, country }) => {
    const target = geocodeModal.participant;
    setGeocodeModal({ open: false, participant: null });
    if (target) {
      await runGeocode(target.participantId, { city, state, country });
    }
  };

  // Function to handle driver assignment
  const handleAssignDriver = async (driverId, participantIds) => {
    // Ensure participantIds is an array and driverId is a single ID
    const updatePromises = participantIds.map((participantId) =>
      updateEventParticipation(participantId, { assignedDriverId: driverId }),
    );

    try {
      await Promise.all(updatePromises);
      console.log("All participants updated successfully.");
      refreshData(); // Refresh data to reflect the changes
    } catch (error) {
      console.error("Error assigning driver:", error);
    } finally {
      setIsAssignDriverModalOpen(false);
      setSelectedParticipants([]);
    }
  };

  const [sortConfig, setSortConfig] = useState({
    key: "needRide",
    direction: "descending",
  });

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleCloseAssignDriverModal = () => {
    setSelectedParticipants([]);
    setSelectedParticipants([]);
    setIsAssignDriverModalOpen(false);
  };

  const availableDrivers = eventParticipations.filter(
    (participant) => participant.isDriver && participant.availability !== "No",
  );

  // Open AssignDriverModal with selected participants
  const openAssignDriverModalForSelected = () => {
    setParticipantToAssign(selectedParticipants); // Assuming selectedParticipants is an array of selected participant IDs

    setIsAssignDriverModalOpen(true);
  };

  const isRSVPOnly = event?.eventSignUpType === "RSVP Only";

  useEffect(() => {
    if (eventParticipations) {
      let sortedParticipants = [...eventParticipations];
      if (sortConfig.key !== null) {
        sortedParticipants.sort((a, b) => {
          if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === "ascending" ? -1 : 1;
          }
          if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === "ascending" ? 1 : -1;
          }
          return 0;
        });
      }
      const filteredParticipants = sortedParticipants.filter(
        (participant) => !participant.isDriver,
      );
      setParticipants(filteredParticipants);
    }
  }, [eventParticipations, sortConfig]);

  const handleCheckboxChange = (participantId, isChecked) => {
    setSelectedParticipants((prevSelected) =>
      isChecked
        ? [...prevSelected, participantId]
        : prevSelected.filter((id) => id !== participantId),
    );
  };

  useEffect(() => {
    const driversWithAvailableCapacity = availableDrivers.map((driver) => {
      const assignedLoad = participants.reduce((total, participant) => {
        return participant.assignedDriverId &&
          participant.assignedDriverId.id === driver.participantId
          ? total + (participant.noOfPpl || 1) // Assume 1 if noOfPpl is not set
          : total;
      }, 0);

      return {
        ...driver,
        availableCapacity: Math.max(0, driver.canTake - assignedLoad),
      };
    });

    setDriversWithCapacity(driversWithAvailableCapacity);

    console.log(
      "Drivers with available capacity:",
      driversWithAvailableCapacity,
    );
  }, [eventParticipations]);

  const canAssignDriver = (driverId, totalPeople) => {
    const driver = driversWithCapacity.find(
      (driver) => driver.participantId === driverId,
    );
    if (!driver) {
      console.error("Driver not found");
      return false;
    }

    const assignedLoad = participants.reduce((total, participant) => {
      return participant.assignedDriverId &&
        participant.assignedDriverId.id === driverId
        ? total + (participant.noOfPpl || 1)
        : total;
    }, 0);
    console.log("Assigned count for", driver.name, ":", assignedLoad);
    console.log("Total people:", totalPeople);
    console.log("Driver can take:", driver.canTake);

    return assignedLoad + totalPeople <= driver.canTake;
  };

  const getSuggestionForParticipant = (participantId) => {
    const suggestion = (suggestions || []).find(
      (suggestion) => suggestion.event_participation_id === participantId,
    );

    if (!suggestion) return null;

    console.log("suggestion", suggestion);

    const driver = driversWithCapacity.find(
      (driver) => driver.participantId === suggestion.driver_id,
    );

    console.log("driver", driver);
    if (!driver || driver.availableCapacity <= 0) {
      return null;
    }

    return suggestion;
  };

  const assignSuggestedDriver = (participant, suggestion) => {
    if (canAssignDriver(suggestion.driver_id, participant.noOfPpl)) {
      handleAssignDriver(suggestion.driver_id, [participant.id]);
    } else {
      alert("Driver will be beyond capacity if this assignment happens.");
    }
  };

  const dismissNonResponderSuggestion = async (participant) => {
    try {
      const token = localStorage.getItem("userToken");
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/event-participations/${participant.id}/dismiss-non-responder-suggestion`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } },
      );
      refreshData();
    } catch (error) {
      console.error("Could not dismiss suggestion", error);
      alert("Could not dismiss suggestion.");
    }
  };

  const removeParticipantFromGroup = async (participant) => {
    if (!window.confirm(`Remove ${participant.name} from this group?`)) return;
    try {
      const token = localStorage.getItem("userToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/participants/${participant.participantId}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) throw new Error("Could not remove participant.");
      refreshData();
    } catch (error) {
      alert(error.message || "Could not remove participant.");
    }
  };

  const NonResponderChip = ({ participant }) => {
    const nonResponder = participant.rsvpNonResponderSuggestion;
    if (!nonResponder) return null;

    return (
      <span
        className="tooltip ml-2 inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-xs font-bold text-orange-700"
        data-tip={nonResponder.message}
      >
        {nonResponder.label}
      </span>
    );
  };

  const NonResponderActions = ({ participant }) => {
    if (!participant.rsvpNonResponderSuggestion) return null;

    return (
      <details className="dropdown dropdown-end ml-2">
        <summary
          className="btn btn-circle btn-xs border border-orange-300 bg-white text-orange-700 shadow-sm hover:bg-orange-50"
          aria-label="Suggestion actions"
        >
          <FontAwesomeIcon icon={faGear} />
        </summary>
        <ul className="menu dropdown-content z-[999] w-60 rounded-xl border border-orange-200 bg-white p-2 shadow-2xl ring-1 ring-orange-100">
          <li>
            <button
              type="button"
              className="rounded-lg text-gray-800 hover:bg-orange-50"
              onClick={() =>
                setPauseParticipant({
                  id: participant.participantId,
                  name: participant.name,
                })
              }
            >
              Pause RSVP texts
            </button>
          </li>
          <li>
            <button
              type="button"
              className="rounded-lg text-gray-800 hover:bg-orange-50"
              onClick={() => removeParticipantFromGroup(participant)}
            >
              Remove from group
            </button>
          </li>
          <li>
            <button
              type="button"
              className="rounded-lg text-gray-800 hover:bg-orange-50"
              onClick={() => dismissNonResponderSuggestion(participant)}
            >
              Dismiss suggestion
            </button>
          </li>
        </ul>
      </details>
    );
  };

  return (
    <div className="card w-full max-w-full overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg">
      {isLoading && <Spinner />}

      {isAssignDriverModalOpen && (
        <AssignDriverModal
          isOpen={isAssignDriverModalOpen}
          onClose={handleCloseAssignDriverModal}
          participants={participantToAssign}
          allParticipants={participants}
          availableDrivers={availableDrivers} // You need to define this list based on your conditions
          onAssign={handleAssignDriver}
        />
      )}

      <div className="p-3 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#004a7e]">
              RSVP responses
            </p>
            <h3 className="text-xl font-black text-gray-900">
              {event?.pingSchedule
                ? "Member Responses"
                : `${participants?.length} Event Members`}
            </h3>
          </div>

          <div
            className="tooltip"
            data-tip="Click checkbox of participants you want to assign"
          >
            {!isRSVPOnly && (
              <button
                onClick={openAssignDriverModalForSelected}
                disabled={selectedParticipants.length === 0 || isEventExpired}
                className="rounded-lg bg-[#004a7e] px-3 py-2 text-sm font-bold text-white hover:bg-[#003a64] disabled:opacity-40"
              >
                Assign Driver
              </button>
            )}
          </div>
        </div>
        <div className="flex justify-between items-center mb-4">
          <div
            className="tooltip"
            data-tip="Click checkbox of participants you want to assign"
          ></div>
        </div>

        {participants?.length > 0 ? (
          <div className="w-full max-w-full overflow-x-auto">
            <table className="table w-full hidden md:table">
              <thead>
                <tr>
                  {!isRSVPOnly && <th></th>} {/* Hide checkbox for RSVP-only */}
                  <th onClick={() => requestSort("name")}>
                    Member{" "}
                    {sortConfig.key === "name" &&
                      (sortConfig.direction === "ascending" ? "▲" : "▼")}
                  </th>
                  {!isRSVPOnly && (
                    <th onClick={() => requestSort("rideLocation")}>
                      Pickup Location{" "}
                      {sortConfig.key === "rideLocation" &&
                        (sortConfig.direction === "ascending" ? "▲" : "▼")}
                    </th>
                  )}
                  <th onClick={() => requestSort("needRide")}>
                    {isRSVPOnly ? "RSVP" : "Need Ride"}{" "}
                    {sortConfig.key === "needRide" &&
                      (sortConfig.direction === "ascending" ? "▲" : "▼")}
                  </th>
                  <th> Message To Admin</th>
                  {!isRSVPOnly && (
                    <th onClick={() => requestSort("assignedDriverId?.name")}>
                      Driver{" "}
                      {sortConfig.key === "assignedDriverId?.name" &&
                        (sortConfig.direction === "ascending" ? "▲" : "▼")}
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {participants.map((participant) => {
                  const suggestion = getSuggestionForParticipant(
                    participant.id,
                  );

                  return (
                    <tr key={participant.id}>
                      {!isRSVPOnly && (
                        <th>
                          <input
                            type="checkbox"
                            disabled={participant.needRide === "No"}
                            className="checkbox checkbox-sm"
                            onChange={(e) =>
                              handleCheckboxChange(
                                participant,
                                e.target.checked,
                              )
                            }
                          />
                        </th>
                      )}
                      <td>
                        <div className="flex items-center gap-3">
                          <div>
                            <div className="font-bold">
                              <a
                                href={`/p/${participant.uniqueCode}?adminPreview=1`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {participant.name}
                                {participant.urlLastViewedAt && (
                                  <span
                                    className="tooltip ml-1"
                                    data-tip={new Date(
                                      participant.urlLastViewedAt,
                                    ).toLocaleString()}
                                  >
                                    <FontAwesomeIcon
                                      icon={faCircleCheck}
                                      className="text-blue-500"
                                    />
                                  </span>
                                )}
                                {participant.noOfPpl > 1 && (
                                  <span className="ml-2 bg-[#004a7e] text-white py-1 px-2  text-xs">
                                    {participant.noOfPpl}{" "}
                                    <FontAwesomeIcon icon={faUsers} />
                                  </span>
                                )}
                              </a>
                              <NonResponderChip participant={participant} />
                              <NonResponderActions participant={participant} />
                            </div>

                            <div className="text-sm">
                              <span className="opacity-50">
                                {formatPhoneNumber(participant.phone)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      {!isRSVPOnly && (
                        <td>
                          <div className="flex items-center gap-2">
                            <span>{participant.rideLocation}</span>
                            {participant.rideLocation &&
                              (hasParticipantCoords(participant) ? (
                                <span
                                  className="text-green-600"
                                  title={
                                    participant.pickupGeocodedAddress ||
                                    "GPS coordinates saved"
                                  }
                                >
                                  <FontAwesomeIcon icon={faMapPin} />
                                </span>
                              ) : (
                                <button
                                  className="btn btn-ghost btn-xs text-[#004a7e]"
                                  onClick={() => handleFillGps(participant)}
                                  title="Fill in GPS coordinates"
                                >
                                  <FontAwesomeIcon icon={faLocationDot} />
                                  <span className="ml-1 text-xs">
                                    Fill in GPS
                                  </span>
                                </button>
                              ))}
                          </div>
                        </td>
                      )}
                      <td>
                        <span
                          className={`badge ${
                            participant.needRide === "Yes"
                              ? "badge-success text-white"
                              : participant.needRide === "No"
                                ? "badge-error text-white"
                                : "badge-warning text-white"
                          } badge-lg`}
                        >
                          {participant.needRide}
                        </span>
                      </td>
                      <td>{participant.messageForAdmin}</td>

                      {!isRSVPOnly && (
                        <td>
                          {participant.assignedDriverId ? (
                            <div className="badge bg-[#ff9100] text-white">
                              <span>
                                <i className="fas fa-car-side"></i> &nbsp;
                                {participant.assignedDriverId.name}
                              </span>{" "}
                            </div>
                          ) : (
                            <div>
                              {participant.needRide === "Yes" ? (
                                suggestion ? (
                                  <div
                                    className="tooltip tooltip-top"
                                    data-tip={`Paired ${suggestion.times_paired} times. Click to assign.`}
                                  >
                                    <button
                                      className="badge bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded cursor-pointer flex items-center gap-2"
                                      onClick={() =>
                                        assignSuggestedDriver(
                                          participant,
                                          suggestion,
                                        )
                                      }
                                    >
                                      <FontAwesomeIcon icon={faLightbulb} />
                                      <span>
                                        Suggestion: {suggestion.driver}
                                      </span>
                                    </button>
                                  </div>
                                ) : (
                                  <span className="badge badge-error text-white">
                                    No Driver
                                  </span>
                                )
                              ) : (
                                <span className="badge badge-error text-white">
                                  No Ride Needed
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="md:hidden">
              {participants.map((participant) => {
                const suggestion = getSuggestionForParticipant(participant.id);

                return (
                  <div
                    key={participant.id}
                    className="relative mb-3 flex w-full max-w-full items-center overflow-visible rounded-lg border border-gray-100 bg-white p-3 shadow-sm"
                  >
                    {participant.needRide === "Yes" && (
                      <div className="ribbon-badge">
                        <span>
                          {participant.assignedDriverId
                            ? "Assigned"
                            : isRSVPOnly
                              ? "Coming" // Change label for RSVP-only events
                              : "Need Ride"}
                        </span>
                      </div>
                    )}

                    {participant.needRide === "No" && (
                      <div className="ribbon-badge-no">
                        <span>Not Coming</span>
                      </div>
                    )}

                    {participant.needRide === "Pending" && (
                      <div className="ribbon-badge-pending">
                        <span>Pending</span>
                      </div>
                    )}
                    {!isRSVPOnly && (
                      <input
                        type="checkbox"
                        disabled={participant.needRide === "No"}
                        className="checkbox checkbox-sm mr-4"
                        checked={selectedParticipants.includes(participant)}
                        onChange={(e) =>
                          handleCheckboxChange(participant, e.target.checked)
                        }
                      />
                    )}
                    <div className="min-w-0 flex-grow break-words pr-2">
                      <div className="flex min-w-0 flex-wrap items-center gap-1">
                        <a
                          href={`/p/${participant.uniqueCode}?adminPreview=1`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="min-w-0 break-words font-bold"
                        >
                          {participant.name}
                          {participant.urlLastViewedAt && (
                            <span
                              className="tooltip ml-1"
                              data-tip={new Date(
                                participant.urlLastViewedAt,
                              ).toLocaleString()}
                            >
                              <FontAwesomeIcon
                                icon={faCircleCheck}
                                className="text-blue-500"
                              />
                            </span>
                          )}{" "}
                          {participant.noOfPpl > 1 && (
                            <span className="ml-2 bg-[#004a7e] text-white py-1 px-2  text-xs">
                              {participant.noOfPpl}{" "}
                              <FontAwesomeIcon icon={faUsers} />
                            </span>
                          )}
                        </a>
                        <NonResponderChip participant={participant} />
                        <NonResponderActions participant={participant} />
                      </div>

                      {!isRSVPOnly && (
                        <div className="break-words text-sm text-gray-500">
                          {participant.rideLocation}
                        </div>
                      )}
                      {!isRSVPOnly &&
                        participant.needRide === "Yes" &&
                        !participant.assignedDriverId &&
                        suggestion && (
                          <button
                            type="button"
                            className="mt-2 inline-flex max-w-full items-center gap-1 rounded-md bg-blue-500 px-2.5 py-1.5 text-xs font-bold uppercase text-white shadow-sm hover:bg-blue-700"
                            onClick={() =>
                              assignSuggestedDriver(participant, suggestion)
                            }
                          >
                            <FontAwesomeIcon icon={faLightbulb} />
                            <span className="truncate">
                              Suggestion: {suggestion.driver}
                            </span>
                          </button>
                        )}
                      <div className="break-words text-sm text-blue-500">
                        {participant.messageForAdmin}
                      </div>
                    </div>
                    {!isRSVPOnly && (
                      <div>
                        {participant.assignedDriverId ? (
                          <div className="driver-ribbon">
                            <span>
                              <i className="fas fa-car-side"></i> &nbsp;
                              {participant.assignedDriverId.name}
                            </span>
                          </div>
                        ) : (
                          participant.needRide === "Yes" && (
                            <div className="driver-ribbon-no">
                              <span>&nbsp; No Driver</span>
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="rounded-lg bg-gray-50 p-5 text-center text-gray-600">
            No participants to display.
          </p>
        )}
      </div>

      <GeocodePromptModal
        isOpen={geocodeModal.open}
        onClose={() => setGeocodeModal({ open: false, participant: null })}
        onSubmit={handleGeocodeModalSubmit}
        title={`Fill in GPS for ${geocodeModal.participant?.name || "member"}`}
        description="Your account doesn't have a default city/state/country yet. Provide it here so we can resolve partial pickup addresses. You can save it permanently in Settings → Default Location."
      />
      <NotificationPauseModal
        participant={pauseParticipant}
        onClose={() => setPauseParticipant(null)}
        onSaved={refreshData}
      />
    </div>
  );
};

export default ParticipantsList;
